import type { DatasheetPoiData, GetYourGuideTour, PoiCategory, PoiData } from '$lib/components/pois/types';
import type {
	AnalyticsEvent,
	PoiAccommodationAvailabilityPayload,
	PoiCategoryFilterContext,
	PoiCtaType,
	PoiPartnerTrackerName,
	PoiPayload,
} from '$lib/services/tracking/googleAnalytics/events';
import { poiCategoryTrackerNames } from '$lib/components/pois/constants';
import { isAccommodationPoi } from '$lib/components/pois/utils';
import { getSponsorConfigByPoiCategory, isAnyPoiCategoryPartner, partner1SponsorConfig, partner2SponsorConfig } from '$lib/constants/sponsoredPoi';
import { booleanToString } from '$lib/services/tracking/googleAnalytics/tracking';
import { formatTrackingValue } from '$lib/utils/formatTrackingValue';

function getBasePoiEventPayload(poi: PoiData): Omit<PoiPayload, 'poi_category'> {
	return {
		poi_partner: undefined,
		poi_name: poi.name,
		poi_country: poi.address?.country?.code ?? undefined,
		poi_city: poi.address?.city ?? undefined,
		poi_postcode: poi.address?.zipCode ?? undefined,
		poi_id: poi.id,
		poi_latitude: `${poi.coordinates.lat}`,
		poi_longitude: `${poi.coordinates.lng}`,
		poi_picture: 'false',
		poi_distinction: '0',
		poi_reviews_number: undefined,
		poi_reviews_rate: undefined,
		poi_michelin: 'false',
		poi_price_category: undefined,
		src_poi_id: poi.srcPoiId,
		user_currency: undefined,
		average_price: undefined,
		charging_spots: undefined,
		charging_spots_details: undefined,
		type_places: undefined,
	};
}

export function getPoiEventPayload(poi: PoiData): PoiPayload {
	switch (poi.__typename) {
		case 'AccommodationMichelinPOI':
			return {
				...getBasePoiEventPayload(poi),
				poi_category: 'accommodation',
				poi_partner: 'michelin',
				poi_picture: poi.images.length > 0 ? 'true' : 'false',
				poi_michelin: 'true',
				poi_price_category: `${poi.priceCategory?.count || 0}`,
				user_currency: poi.priceCategory?.currency,
				fully_booked: poi.isAvailable !== null ? booleanToString(!poi.isAvailable) : undefined,
			};
		case 'AccommodationPOI':
			return {
				...getBasePoiEventPayload(poi),
				poi_category: 'accommodation',
				poi_partner: 'booking',
				poi_picture: poi.images.length > 0 ? 'true' : 'false',
				poi_reviews_number: poi.review?.count?.toString() ?? undefined,
				poi_reviews_rate: poi.review?.score.toString() ?? undefined,
				poi_distinction: `${poi.stars ?? 0}`,
				user_currency: poi.startingPrice?.currency,
				fully_booked: poi.isAvailable !== null ? booleanToString(!poi.isAvailable) : undefined,
			};
		case 'ParkingPOI':
			return {
				...getBasePoiEventPayload(poi),
				poi_category: 'parking',
			};
		case 'RestaurantMichelinPOI':
			return {
				...getBasePoiEventPayload(poi),
				poi_category: 'restaurant',
				poi_partner: 'michelin',
				poi_picture: poi.images.length > 0 ? 'true' : 'false',
				poi_michelin: 'true',
				poi_distinction: `${poi.distinctionStars ?? 0 + (poi.bibGourmand ? 1 : 0)}`,
				poi_price_category: `${poi.priceCategory?.count || 0}`,
				user_currency: poi.priceCategory?.currency,
			};
		case 'RestaurantPOI':
			return {
				...getBasePoiEventPayload(poi),
				poi_category: 'restaurant',
				poi_partner: 'the_fork',
				poi_picture: poi.images.length > 0 ? 'true' : 'false',
				poi_reviews_number: poi.review?.count?.toString() ?? undefined,
				poi_reviews_rate: poi.review?.score.toString() ?? undefined,
				average_price: poi.averagePrice?.amount.toString() ?? undefined,
				user_currency: poi.averagePrice?.currency,
			};
		case 'TourismPOI':
			return {
				...getBasePoiEventPayload(poi),
				poi_category: 'tourism',
				poi_partner: 'green_guide',
				poi_picture: poi.images.length > 0 ? 'true' : 'false',
				poi_distinction: `${poi.distinctionStars ?? 0}`,
			};

		case 'ServiceGasStationPOI':
			return {
				...getBasePoiEventPayload(poi),
				poi_category: 'gasStation',
			};

		case 'ServicePOI':
			return {
				...getBasePoiEventPayload(poi),
				poi_category: 'gasStation',
			};

		case 'ServiceEVPOI':
			return {
				...getBasePoiEventPayload(poi),
				poi_category: 'ev',
				charging_spots: `${poi.spots_count}`,
				charging_spots_details: poi.connectors.join('|'),
			};

		case 'ServiceRestStopPOI':
			return {
				...getBasePoiEventPayload(poi),
				poi_category: 'restStop',
				poi_is_family_friendly: poi.isFamilyFriendly,
				poi_is_village_etape: poi.isVillageEtape,
			};

		case 'PartnerPOI':
			return {
				...getBasePoiEventPayload(poi),
				poi_category: partner1SponsorConfig.tracking.poiCategory,
				poi_partner: partner1SponsorConfig.tracking.poiPartner,
			};
		case 'Partner2POI':
			return {
				...getBasePoiEventPayload(poi),
				poi_category: partner2SponsorConfig.tracking.poiCategory,
				poi_partner: partner2SponsorConfig.tracking.poiPartner,
			};
	}
}

export function getPoiListTrackingEvent(poi: PoiData, ctaType: PoiCtaType): AnalyticsEvent {
	return {
		action: 'call_to_action',
		payload: {
			...getPoiEventPayload(poi),
			cta_type: ctaType,
		},
	};
}

export function getPoiMapTrackingEvent(poi: PoiData): AnalyticsEvent {
	return {
		action: 'poi_map_selection',
		payload: {
			...getPoiEventPayload(poi),
		},
	};
}

export function getViewMoreOnTrackingEvent(): AnalyticsEvent {
	return {
		action: 'view_more',
		payload: {
			action: 'click',
		},
	};
}

export function getPoiEventGetYourGuidePayload(getYourGuideTour: GetYourGuideTour) {
	const { price, review, title, tourId } = getYourGuideTour;
	return {
		activity_id: tourId.toString(),
		activity_price_amount: price.amount.toString(),
		activity_reviews_number: review?.count?.toString(),
		activity_reviews_rate: review?.score.toFixed(1),
		activity_title: title,
		user_currency: price.currency,
	};
}

export function buildAccommodationPoiTrackingData(
	poi: DatasheetPoiData,
): PoiAccommodationAvailabilityPayload {
	return {
		poi_city: poi.address.city,
		poi_country: poi.address.country?.code ?? '',
		poi_distinction: isAccommodationPoi(poi) ? poi?.stars?.toString() : '',
		poi_id: isAccommodationPoi(poi) && poi.id ? poi.id : '',
		poi_name: poi.name,
		poi_postcode: poi.address.zipCode,
		poi_price_category: isAccommodationPoi(poi) ? poi.priceCategory?.count.toString() : '',
		poi_reviews_number: isAccommodationPoi(poi) ? poi?.review?.count?.toString() : '',
		poi_reviews_rate: isAccommodationPoi(poi) ? poi?.review?.score?.toString() : '',
	};
}

export function getPoiPartnerTrackingEvent(poi: PoiData): AnalyticsEvent {
	return { action: 'call_to_action', payload: { action: 'click', ...getPoiEventPayload(poi) } };
}

function formatPoiCategorySelected(poiCategory: PoiCategory[]) {
	const poiCategorySelected = poiCategory.map(category => poiCategoryTrackerNames[category]);
	return formatTrackingValue(poiCategorySelected);
}

export function getPoiCategoryToggleTrackingEvent({
	poiCategory,
	context,
	selected,
	singleCategoryMode,
	updatedSelectedCategories,
}: {
	poiCategory: PoiCategory;
	context: PoiCategoryFilterContext;
	selected: boolean;
	singleCategoryMode: boolean;
	updatedSelectedCategories: PoiCategory[];
}): AnalyticsEvent {
	let poiPartner: PoiPartnerTrackerName | undefined;
	if (isAnyPoiCategoryPartner(poiCategory)) {
		const sponsorConfig = getSponsorConfigByPoiCategory(poiCategory);
		poiPartner = sponsorConfig?.tracking.poiPartner;
	}

	return {
		action: 'toggle_poi_switch',
		payload: {
			poi_category: poiCategoryTrackerNames[poiCategory],
			poi_partner: poiPartner,
			context,
			action: selected ? 'on' : 'off',
			...(singleCategoryMode
				? {}
				: {
						list_poi_category_selected: formatPoiCategorySelected(updatedSelectedCategories),
					}),
		},
	};
}
